<script setup>
import { ref, inject } from 'vue';
import { Head } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useFoodflowSessionStore } from '@/stores/foodflowSession';
import { useDateFormatter } from '@/composables/dateFormatter';
import { trans } from 'laravel-vue-i18n';
import FoodFlowPage from '@/components/FoodFlow/FoodFlowPage.vue';
import FoodFlowBadge from '@/components/FoodFlow/FoodFlowBadge.vue';
import NewSessionWizard from '@/components/FoodFlow/NewSessionWizard.vue';
import SessionDetail from '@/components/FoodFlow/SessionDetail.vue';
import { FOODFLOW_MODAL_CARD_CLASS_NARROW } from '@/utils/foodFlowModal';

const { search } = useFoodflowSessionStore();
const { all } = storeToRefs(useFoodflowSessionStore());
const { formatDateTime } = useDateFormatter();

const modals = inject('modals');
const openExportModal = () => modals.show('ExportSessionsModal', { props: { bodyClass: FOODFLOW_MODAL_CARD_CLASS_NARROW } });

// view: 'list' | 'wizard' | 'detail'
const view = ref('list');
const selectedSessionHash = ref(null);

const params = ref({
    term: '',
    type: null,
    period: null,
    page: 1,
    sort_by: null,
    per_page: 10,
});

const searchSessions = () => {
    search({
        ...params.value,
    });
};
searchSessions();

const goToPage = (page) => {
    params.value.page = page;
    searchSessions();
};

const sessionTotalWeight = (session) => session.entries.reduce((sum, e) => sum + parseFloat(e.weight), 0);
const sessionWeightLabel = (session) => `${session.type === 'intake' ? '+' : '-'}${sessionTotalWeight(session).toFixed(1)} ${trans('label.lbs')}`;
const entriesLabel = (session) => {
    const count = session.entries?.length ?? 0;
    return `${count} ${count === 1 ? trans('label.entry') : trans('label.entries')}`;
};

const startNewSession = () => {
    view.value = 'wizard';
};

const openSession = (session) => {
    selectedSessionHash.value = session.hash;
    view.value = 'detail';
};

const backToList = () => {
    view.value = 'list';
    selectedSessionHash.value = null;
    searchSessions();
};
</script>

<template>
<FoodFlowPage v-if="view === 'list'" max-width-class="max-w-[1100px]">
    <Head :title="$t('label.sessions')" />
    <template #title>{{ $t('label.sessions') }}</template>
    <template #actions>
        <button type="button" class="foodflow-btn-secondary" @click="openExportModal">
            <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"><path
                    d="M7 1v8M4 6l3 3 3-3M2 10v2a1 1 0 001 1h8a1 1 0 001-1v-2"
                    stroke="#375475"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round" /></svg>
            {{ $t('label.export') }}
        </button>
        <button type="button" class="foodflow-btn-primary" @click="startNewSession"><FontAwesomeIcon icon="plus" class="mr-1" />{{ $t('label.new_session') }}</button>
    </template>

    <div class="foodflow-filter-bar">
        <div class="search-group">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span class="text-[14px] text-[#94a3b8]">🔍</span>
            <StyledSearch
                v-model="params.term"
                v-debounce:500ms="searchSessions"
                name="search"
                :placeholder="$t('label.search_sessions')"
                :debounce-events="['input']" />
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.type') }}</span>
            <select v-model="params.type" class="foodflow-filter-select" @change="searchSessions">
                <option :value="null">{{ $t('label.all_types') }}</option>
                <option value="intake">{{ $t('label.intake') }}</option>
                <option value="outtake">{{ $t('label.outtake') }}</option>
            </select>
        </div>
        <div class="foodflow-filter-group">
            <span class="foodflow-filter-label">{{ $t('label.period') }}</span>
            <select v-model="params.period" class="foodflow-filter-select" @change="searchSessions">
                <option :value="null">{{ $t('label.all_time') }}</option>
                <option value="week">{{ $t('label.this_week') }}</option>
                <option value="month">{{ $t('label.this_month') }}</option>
            </select>
        </div>
    </div>

    <div class="foodflow-table">
        <div class="foodflow-table-header sessions-table-grid">
            <div class="foodflow-table-header-cell hidden sm:block">{{ $t('label.date') }}</div>
            <div class="foodflow-table-header-cell">{{ $t('label.type') }}</div>
            <div class="foodflow-table-header-cell">{{ $t('label.supplier_recipient') }}</div>
            <div class="foodflow-table-header-cell hidden md:block">{{ $t('label.entries') }}</div>
            <div class="foodflow-table-header-cell text-right">{{ $t('label.weight') }}</div>
            <div />
        </div>
        <template v-if="all?.data?.length">
            <div
                v-for="session in all.data"
                :key="session.hash"
                class="foodflow-table-row foodflow-table-row-clickable sessions-table-grid"
                @click="openSession(session)">
                <div class="sessions-table-cell-date">{{ formatDateTime(session.session_date) }}</div>
                <div class="foodflow-table-cell">
                    <FoodFlowBadge :variant="session.type === 'intake' ? 'green' : 'red'" dot>
                        {{ session.type === 'intake' ? $t('label.intake') : $t('label.outtake') }}
                    </FoodFlowBadge>
                </div>
                <div class="sessions-table-cell-name" :title="session.contact?.name">{{ session.contact?.name }}</div>
                <div class="sessions-table-cell-entries">{{ entriesLabel(session) }}</div>
                <div class="sessions-table-cell-weight" :class="session.type === 'intake' ? 'text-[#2d6b1a]' : 'text-[#a83430]'">
                    {{ sessionWeightLabel(session) }}
                </div>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <div class="sessions-table-cell-chevron">›</div>
            </div>
        </template>
        <div v-else class="foodflow-table-empty">
            {{ $t('label.no_sessions_yet') }}
        </div>
    </div>

    <div v-if="all?.last_page > 1" class="foodflow-pagination">
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page <= 1"
            @click="goToPage(all.current_page - 1)">{{ $t('label.previous') }}</button>
        <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
        <span class="text-[12px] text-[#64748b]">{{ all.current_page }} / {{ all.last_page }}</span>
        <button
            type="button"
            class="foodflow-btn-muted"
            :disabled="all.current_page >= all.last_page"
            @click="goToPage(all.current_page + 1)">{{ $t('label.next') }}</button>
    </div>
</FoodFlowPage>
<NewSessionWizard v-else-if="view === 'wizard'" @close="backToList" />
<SessionDetail v-else-if="view === 'detail'" :session-hash="selectedSessionHash" @close="backToList" />
</template>
